import type { MdsPatientRow } from 'src/services/documentService/getMdsPatients';

/**
 * BRK-1851: derives the due-status chips for a patient row from the
 * server-computed due-intelligence fields (UX spec colors):
 * five-day window amber days 1-5 / red 6-8; completion amber <=7d / red
 * overdue (suppressed once the worksheet is SUBMITTED); quarterly amber
 * approaching (<=14d) / red past; GG items missing + New admission informational.
 * Pure function — this is where the chip rules live, and where they're tested.
 */

export type DueChip = {
  key: string;
  label: string;
  color: 'default' | 'info' | 'warning' | 'error';
};

const formatDate = (iso: string): string => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

export const buildDueChips = (row: MdsPatientRow, today: Date = new Date()): DueChip[] => {
  const chips: DueChip[] = [];
  const due = row.dueStatus;
  if (!due) {
    return chips;
  }
  const submitted = row.worksheetStatus?.state === 'SUBMITTED';

  // server contract bounds these 1-8 / >=0; guard anyway so a refresh-lag
  // edge value can't render a nonsensical "Day -1" or "ARD in -2d" chip
  if (due.fiveDayWindowDay != null && due.fiveDayWindowDay >= 1 && due.fiveDayWindowDay <= 8) {
    chips.push({
      key: 'fiveDay',
      label: `5-Day window: Day ${due.fiveDayWindowDay}/8`,
      color: due.fiveDayWindowDay >= 6 ? 'error' : 'warning',
    });
  }

  if (due.ardInDays != null && due.ardInDays >= 0) {
    chips.push({
      key: 'ard',
      label: due.ardInDays === 0 ? 'ARD today' : `ARD in ${due.ardInDays}d`,
      color: due.ardInDays <= 7 ? 'warning' : 'default',
    });
  }

  // completion deadline only matters until a worksheet version is submitted
  if (!submitted && due.completeInDays != null && due.completeByDate) {
    if (due.completeInDays < 0) {
      chips.push({
        key: 'complete',
        label: `Overdue by ${Math.abs(due.completeInDays)}d`,
        color: 'error',
      });
    } else if (due.completeInDays <= 7) {
      chips.push({
        key: 'complete',
        label: `Complete by ${formatDate(due.completeByDate)}`,
        color: 'warning',
      });
    }
  }

  if (due.quarterlyDueDate) {
    const [year, month, day] = due.quarterlyDueDate.split('-').map(Number);
    const dueDate = new Date(year, (month || 1) - 1, day || 1);
    const msPerDay = 24 * 60 * 60 * 1000;
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const daysUntil = Math.round((dueDate.getTime() - startOfToday.getTime()) / msPerDay);
    if (daysUntil < 0) {
      chips.push({
        key: 'quarterly',
        label: `Quarterly overdue (${formatDate(due.quarterlyDueDate)})`,
        color: 'error',
      });
    } else if (daysUntil <= 14) {
      chips.push({
        key: 'quarterly',
        label: `Quarterly due ${formatDate(due.quarterlyDueDate)}`,
        color: 'warning',
      });
    }
  }

  if (due.missingGgCount != null && due.missingGgCount > 0) {
    chips.push({
      key: 'missingGg',
      // BRK-2163: name the items, so the count can't be read as a GG/function score
      label: `GG items missing: ${due.missingGgCount}`,
      color: 'default',
    });
  }

  if (due.newAdmission) {
    chips.push({ key: 'newAdmission', label: 'New admission', color: 'info' });
  }

  return chips;
};
